import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type { SubscriberListRef } from '@/features/subscribers';
import { PersonAvatar } from '@/shared/components/PersonAvatar';
import { pluralise } from '@/shared/lib/format';
import type { Draft } from '../api/draftsApi';
import { headline, kindLabel } from '../model/draftView';
import { enterSx, tagSx } from './styles';

type Props = { drafts: Draft[]; lists: SubscriberListRef[]; selected: string; onSelect: (id: string) => void };

const DAY_MONTH = new Intl.DateTimeFormat('en-SG', { day: 'numeric', month: 'short' });

/** One row per message: who asked, when, its first line, the list and how many it goes to. */
export function DraftList({ drafts, lists, selected, onSelect }: Props) {
  const reach = (d: Draft) => (d.deliveries.length || (lists.find((l) => l.id === d.list.id)?.optedInCount ?? d.list.count));
  return (
    <Stack component="ul" aria-label="Messages" sx={{ listStyle: 'none', m: 0, p: 0 }}>
      {drafts.map((d, i) => {
        const isOn = d.id === selected;
        return (
          <li key={d.id}>
            <ButtonBase aria-current={isOn ? 'true' : undefined} onClick={() => { onSelect(d.id); }} sx={{ ...rowSx(isOn), ...enterSx, animationDelay: `${i * 35}ms` }}>
              <PersonAvatar name={d.requester.name} size={32} />
              <Stack sx={{ flexGrow: 1, minWidth: 0, gap: 0.5, textAlign: 'left' }}>
                <Stack direction="row" sx={{ alignItems: 'baseline', gap: 1 }}>
                  <Typography variant="caption" noWrap sx={{ color: 'text.secondary', flexGrow: 1 }}>{d.requester.name}</Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary', flexShrink: 0 }}>{DAY_MONTH.format(new Date(d.createdAt))}</Typography>
                </Stack>
                <Typography variant="body2" sx={titleSx}>{headline(d.finalText) || d.id}</Typography>
                <Stack direction="row" sx={{ alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                  <Box component="span" sx={{ ...tagSx, overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '60%' }}>{d.list.name}</Box>
                  <Typography variant="caption" noWrap sx={{ color: 'text.secondary' }}>{pluralise(reach(d), 'recipient')} · {kindLabel(d)}</Typography>
                </Stack>
              </Stack>
            </ButtonBase>
          </li>
        );
      })}
    </Stack>
  );
}

const rowSx = (isOn: boolean) => ({
  width: '100%',
  alignItems: 'flex-start',
  gap: 1.25,
  px: 2,
  py: 1.5,
  borderBottom: 1,
  borderColor: 'divider',
  bgcolor: isOn ? 'surface.accentSoft' : 'transparent',
  boxShadow: isOn ? 'inset 3px 0 0 var(--mui-palette-primary-main)' : 'none',
  transition: 'background-color 150ms, box-shadow 150ms',
  '&:hover': { bgcolor: isOn ? 'surface.accentSoft' : 'surface.sidebar' },
});

const titleSx = {
  fontWeight: 600,
  lineHeight: 1.4,
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
} as const;
